import { PrefillType } from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CareCreditIntegrationCustom, CareCreditIntegrationV1 } from '@frontend/api-care-credit-integration';

dayjs.extend(utc);

// Define a purl transaction type because when
// this query is called from the coupled flow
// a weave invoice is passed in. but when it is
// called from the decoupled flow, we don't have a
// weave invoice to pass in. so it makes sense to
// define what this hook needs and let callers decide
// how to satisfy the interface.
export type CareCreditPurlTransaction = {
  paymentRequestId: string;
  purchaseAmountInCents: number;
  invoiceNumber: string;
  serviceDate: string;
};

export type UseCareCreditPurlQueryProps = {
  locationId: string | undefined;
  prefillType?: PrefillType;
  person: Person | undefined;
  careCreditPurlTransaction?: CareCreditPurlTransaction;
  isDecoupled: boolean;
  userId: string;
};

export type UseCareCreditPurlQueryOptions = {
  enabled?: boolean;
};

export const useCareCreditPurlQuery = (
  {
    locationId,
    prefillType = PrefillType.PREFILL_TYPE_UNSPECIFIED,
    person,
    careCreditPurlTransaction,
    isDecoupled,
    userId,
  }: UseCareCreditPurlQueryProps,
  { enabled }: UseCareCreditPurlQueryOptions = {}
) => {
  const { firstName, lastName, email, contactNumber, dateOfBirth, addressLine1, city, state, zipCode } =
    CareCreditIntegrationCustom.getPatientQueryFields(person) || {};
  const resolvedPrefillType =
    prefillType ??
    (careCreditPurlTransaction ? PrefillType.PREFILL_TYPE_TRANSACTION_PROCESS : PrefillType.PREFILL_TYPE_UNSPECIFIED);

  const {
    data: purl,
    isLoading: isPurlLoading,
    error: purlError,
    isSuccess: isPurlSuccess,
  } = CareCreditIntegrationV1.Queries.useGeneratePatientDataPurlQuery<
    unknown,
    CareCreditIntegrationV1.Types.GeneratePatientDataPurlIO['output']
  >({
    request: {
      isDecoupled,
      userId,
      locationId,
      // this value is useful for "coupled" transactions, because
      // we need to be able to tie the weave invoice id to the
      // care credit transaction in the GetSessionActivityByReferenceId
      // endpoint. it's not useful for "decoupled" payments because
      // there is no weave data that a decoupled care credit payment
      // is tied to. in "decoupled" flows the correct value for callers
      // to pass in here is an empty string
      paymentRequestId: careCreditPurlTransaction?.paymentRequestId,
      personId: person?.personId,
      prefillType: resolvedPrefillType,
      applicants: [
        {
          firstName,
          lastName,
          dateOfBirth,
          estimatedFee: careCreditPurlTransaction?.purchaseAmountInCents,
          address: {
            addressLine1,
            city,
            state,
            zipCode,
          },
          contactInfo: {
            emailAddress: email,
            homePhone: contactNumber,
          },
        },
      ],
      transactionInfo: {
        purchaseAmount: careCreditPurlTransaction?.purchaseAmountInCents,
        procedureDate: careCreditPurlTransaction?.serviceDate
          ? dayjs(careCreditPurlTransaction?.serviceDate).local().format('YYYY-MM-DD')
          : undefined,
        // https://developer.syf.com/api-reference/patient-data-service-v1#operation/prefillPatientData
        // In CareCredit's TTP portal, there is a field called Invoice Number that is required. This is
        // distinctly different from the invoiceNumber field that gets mentioned in the docs link above.
        // I don't see any reason to pass a uuid into a customer-facing portal, when we can handle it
        // internally.
        //
        // The CC TTP portal suggests the following guidance for this field:
        // > For security, do not enter private information such as SSN or credit card numbers.
        // > If no invoice number, type "none".
        //
        practiceMemo: careCreditPurlTransaction?.invoiceNumber?.trim() || 'none',
      },
    },
    options: {
      enabled: !!locationId && !!person && enabled !== false,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  return { purl, isPurlLoading, purlError, isPurlSuccess };
};
